import { GQL } from '@aline/graphql';
import { z } from 'zod';

const crmConnectionIssuePayloadSchema = z.object({
  /** The CRM's own explanation, when it gave one (e.g. Salesforce refusing the signing key). */
  detail: z.string().nullable(),
  integration: z.enum(Object.values(GQL.Integration)),
  issueKind: z.enum(Object.values(GQL.CrmConnectionIssueKind)),
});

/** What is wrong with a synced property's CRM field. */
export const CRM_FIELD_ISSUE_KIND = {
  createFailed: 'CreateFailed',
  needsAccess: 'NeedsAccess',
} as const;
export type CrmFieldIssueKind = (typeof CRM_FIELD_ISSUE_KIND)[keyof typeof CRM_FIELD_ISSUE_KIND];

const crmFieldIssuePayloadSchema = z.object({
  detail: z.string().nullable(),
  integration: z.enum(Object.values(GQL.Integration)),
  issueKind: z.enum(Object.values(CRM_FIELD_ISSUE_KIND)),
  properties: z.array(z.object({ questionId: z.string().uuid(), title: z.string() })).min(1),
  reconnect: z.boolean(),
  setupUrl: z.string().url().nullable(),
});

const crmRecordDeletedPayloadSchema = z.object({
  integration: z.enum(Object.values(GQL.Integration)),
  opportunityId: z.string().min(1),
  workflowId: z.string().uuid(),
});

/**
 * One rolling notification per document owner: detections join it until the owner reads or
 * dismisses it, and the next detection after that opens a new window.
 */
const signatureDetectedPayloadSchema = z.object({
  documentCount: z.number().int().positive(),
  latestDocumentId: z.string().uuid(),
  /** The name when it was raised, so the bell can show it without loading the document. */
  latestDocumentName: z.string(),
  ownerId: z.string().uuid(),
  /** How many of the documents detection also set to Signed (not templates or workflow signing files). */
  statusAppliedCount: z.number().int().nonnegative(),
  windowId: z.string().uuid(),
});

/**
 * Every kind's payload shape: the contract the server writes and the client reads. Keyed on the
 * GraphQL enum, so a new kind fails to compile until it has one. How a kind is deduped, resolved
 * and dismissed is server-only and lives in the server's inbox notification registry.
 */
const INBOX_NOTIFICATION_PAYLOAD_SCHEMAS = {
  [GQL.InboxNotificationKind.CrmConnectionIssue]: crmConnectionIssuePayloadSchema,
  [GQL.InboxNotificationKind.CrmFieldIssue]: crmFieldIssuePayloadSchema,
  [GQL.InboxNotificationKind.CrmRecordDeleted]: crmRecordDeletedPayloadSchema,
  [GQL.InboxNotificationKind.SignatureDetected]: signatureDetectedPayloadSchema,
} as const satisfies Record<GQL.InboxNotificationKind, z.ZodType>;

export type InboxNotificationPayload<TKind extends GQL.InboxNotificationKind> = z.infer<
  (typeof INBOX_NOTIFICATION_PAYLOAD_SCHEMAS)[TKind]
>;

/** A payload narrowed by its kind, so a `switch` on `kind` types the payload in each branch. */
export type ParsedInboxNotification = {
  [TKind in GQL.InboxNotificationKind]: {
    readonly kind: TKind;
    readonly payload: InboxNotificationPayload<TKind>;
  };
}[GQL.InboxNotificationKind];

/** Throws when the kind is unknown or the payload does not match its kind's schema. */
export function parseInboxNotification(
  kind: GQL.InboxNotificationKind,
  payload: unknown
): ParsedInboxNotification {
  switch (kind) {
    case GQL.InboxNotificationKind.CrmConnectionIssue:
      return { kind, payload: crmConnectionIssuePayloadSchema.parse(payload) };
    case GQL.InboxNotificationKind.CrmFieldIssue:
      return { kind, payload: crmFieldIssuePayloadSchema.parse(payload) };
    case GQL.InboxNotificationKind.CrmRecordDeleted:
      return { kind, payload: crmRecordDeletedPayloadSchema.parse(payload) };
    case GQL.InboxNotificationKind.SignatureDetected:
      return { kind, payload: signatureDetectedPayloadSchema.parse(payload) };
    // The stored kind is plain text, so a value outside the enum can reach here.
    default:
      throw new Error(`Unknown inbox notification kind: ${String(kind)}`);
  }
}

/** Null when the payload does not match its kind's schema, for readers that skip what they can't show. */
export function safeParseInboxNotification(
  kind: GQL.InboxNotificationKind,
  payload: unknown
): ParsedInboxNotification | null {
  try {
    return parseInboxNotification(kind, payload);
  } catch {
    return null;
  }
}
